/**
 * ARK ETF tab CSV export — Holdings columns plus trade date and ARK holdings metrics.
 */
import { escapeCsvCell } from "./csvFileHelpers";
import {
  STOCK_CSV_COLUMN_IDS,
  formatStockEarningsDateForCsv,
  stockRecordToCsvCells,
} from "./stockCsv";

const ARK_ETF_CSV_EXTRA_LIST = ["trade_date", "ark_market_value", "ark_weight"];
const ARK_ETF_CSV_EXTRA_IDS = new Set(ARK_ETF_CSV_EXTRA_LIST);

/**
 * Full Holdings CSV columns with ARK fields before earnings date (matches ARK ETF table).
 */
export function getStockArkEtfCsvColumnIds() {
  const base = STOCK_CSV_COLUMN_IDS.filter((id) => !ARK_ETF_CSV_EXTRA_IDS.has(id));
  const earningsIdx = base.indexOf("earnings_date");
  if (earningsIdx >= 0) {
    return [
      ...base.slice(0, earningsIdx),
      ...ARK_ETF_CSV_EXTRA_LIST,
      ...base.slice(earningsIdx),
    ];
  }
  const nameIdx = base.indexOf("name");
  const insertAt = nameIdx >= 0 ? nameIdx + 1 : 1;
  return [...base.slice(0, insertAt), ...ARK_ETF_CSV_EXTRA_LIST, ...base.slice(insertAt)];
}

export const STOCK_ARK_ETF_CSV_HEADER_LINE = getStockArkEtfCsvColumnIds().join(",");

/**
 * @param {Record<string, unknown>} row
 * @returns {string[]}
 */
export function stockArkEtfRecordToCsvCells(row) {
  const stockByField = Object.fromEntries(
    STOCK_CSV_COLUMN_IDS.map((id, i) => [id, stockRecordToCsvCells(row)[i]])
  );
  return getStockArkEtfCsvColumnIds().map((fieldId) => {
    if (fieldId === "trade_date") {
      const raw = row.trade_date;
      if (!raw) return "";
      const formatted = formatStockEarningsDateForCsv(raw);
      return formatted || String(raw).trim();
    }
    if (fieldId === "ark_market_value") {
      return row.ark_market_value != null ? String(row.ark_market_value).trim() : "";
    }
    if (fieldId === "ark_weight") {
      return row.ark_weight != null ? String(row.ark_weight).trim() : "";
    }
    return stockByField[fieldId] ?? "";
  });
}

/**
 * @param {Record<string, unknown>[]} rows
 * @returns {string}
 */
export function buildStockArkEtfCsvExportText(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const lines = [
    STOCK_ARK_ETF_CSV_HEADER_LINE,
    ...list.map((row) => stockArkEtfRecordToCsvCells(row).map(escapeCsvCell).join(",")),
  ];
  return lines.join("\r\n");
}
